<template>
    <div v-if="!loading" class="form-group">
        <label :for="elementId" class="control-label">{{ label }}</label>
        <select :id="elementId" v-model="assistantId"
                class="form-control" name="assistant"
                style="width: 100%;"></select>

        <div v-if="selectedAssistantIsMultiChar" class="mt-3 character-selection">
            <label class="control-label">Which character speaks first?</label>
            <div v-for="(character, index) in selectedAssistantCharacters"
                 :key="index"
                 class="form-check">
                <input
                    :id="`char-first-${elementId}-${index}`"
                    v-model="firstCharacterIndex"
                    :value="index"
                    class="form-check-input"
                    type="radio"
                    :name="`firstCharacter-${elementId}`"
                />
                <label :for="`char-first-${elementId}-${index}`" class="form-check-label">
                    {{ character.name }}
                </label>
            </div>
        </div>
    </div>
</template>

<script setup>
import {computed, nextTick, onMounted, ref, watch} from 'vue';
import Openai from '../../common/openai';

const props = defineProps({
    id: String,
    firstCharacterIndex: {
        type: Number,
        default: 0,
    },
    type: String,
    idIndex: String,
    label: {
        type: String,
        default: 'AI Assistant',
    },
});

const emit = defineEmits(['handleChange']);
const loading = ref(true);

const normalizeId = (id) => (id === null || id === undefined) ? null : String(id);

const assistantId = ref(props.id || null);
const selectedAssistant = ref(null);
const firstCharacterIndex = ref(props.firstCharacterIndex || 0);
const elementId = ref((props.idIndex) ? `ai-assistant-select-${props.idIndex}` : 'ai-assistant-select');

watch(() => props.id, (newId) => {
    if (newId !== assistantId.value) {
        assistantId.value = newId || null;
    
        const $selectEl = $(`#${elementId.value}`);
        if ($selectEl.length && $selectEl.data('select2')) {
            $selectEl.val(assistantId.value).trigger('change.select2');
        }
    }
}, {immediate: false});

watch(() => props.firstCharacterIndex, (newVal) => {
    if (typeof newVal === 'number') {
        firstCharacterIndex.value = newVal;
    }
});

const selectedAssistantIsMultiChar = computed(() => {
    return selectedAssistant.value?.type === 'feedback_multi_character';
});

const selectedAssistantCharacters = computed(() => {
    return selectedAssistant.value?.characters || [];
});

async function fetchAssistants() {
    const openai = new Openai();

    // If type is 'feedback', fetch both 'feedback' and 'feedback_multi_character'
    let allAssistants = [];

    if (props.type === 'feedback') {
        const feedbackResp = await openai.getAssistantsByType('feedback');
        const multiCharResp = await openai.getAssistantsByType('feedback_multi_character');
        allAssistants = [...feedbackResp, ...multiCharResp];
    } else if (props.type) {
        allAssistants = await openai.getAssistantsByType(props.type);
    } else {
        allAssistants = await openai.getAssistantsByType('feedback');
    }

    const assistants = allAssistants.map(item => ({
        // Use database ID as the primary identifier
        id: item.id,
        // Store assistant_id for matching legacy content
        assistant_id: item.assistant_id ?? item.id,
        text: `${item.name} (${item.model || 'unknown model'}) - ${item.type}`,
        type: item.type,
        characters: item.characters || null,
    }));

    if (assistants.length === 1) {
        assistantId.value = assistants[0].id;
    }
    loading.value = false;
    return assistants;
}

onMounted(async () => {
    const assistants = await fetchAssistants();

    $(() => {
        const $selectEl = $(`#${elementId.value}`);
        const assistantIdStr = normalizeId(assistantId.value);

        const data = assistants.map(tag => {
            const tagIdStr = normalizeId(tag.id);
            const tagAssistantIdStr = normalizeId(tag.assistant_id);
            // Match against BOTH database id and OpenAI assistant_id for backwards compatibility
            const isSelected = tagIdStr === assistantIdStr || tagAssistantIdStr === assistantIdStr;

            return {
                id: tag.id,
                text: tag.text,
                selected: isSelected,
            };
        });

        $selectEl.select2({
            data: [{id: '', text: '', selected: !assistantIdStr}, ...data],
            allowClear: true,
            placeholder: '',
        });
    
        if (assistantId.value) {
            const assistantIdStr = normalizeId(assistantId.value);
      
            const matchingAssistant = assistants.find(a =>
                normalizeId(a.id) === assistantIdStr || normalizeId(a.assistant_id) === assistantIdStr,
            );
            if (matchingAssistant) {
                $selectEl.val(normalizeId(matchingAssistant.id)).trigger('change.select2');
            }
        }
    
        $selectEl.on('change', function () {
            const selected = $selectEl.select2('data');
            const selectedId = normalizeId(selected?.[0]?.id) || null;

            selectedAssistant.value = selectedId ? assistants.find(a =>
                normalizeId(a.id) === selectedId || normalizeId(a.assistant_id) === selectedId,
            ) : null;
            assistantId.value = selectedId;

            nextTick(() => {
                emit(
                    'handleChange',
                    assistantId.value,
                    firstCharacterIndex.value,
                    selectedAssistant.value?.type || null,
                );
            });
        });
    });

    if (assistantId.value) {
        const assistantIdStr = normalizeId(assistantId.value);
        selectedAssistant.value = assistants.find(a =>
            normalizeId(a.id) === assistantIdStr || normalizeId(a.assistant_id) === assistantIdStr,
        );

        nextTick(() => {
            emit('handleChange', assistantId.value, firstCharacterIndex.value, selectedAssistant.value?.type || null);
        });
    }

});

watch(firstCharacterIndex, () => {
    if (selectedAssistant.value) {
        emit('handleChange', assistantId.value, firstCharacterIndex.value, selectedAssistant.value?.type || null);
    }
});
</script>
